import React from 'react';
import { TrendingUp, Users, Cpu } from 'lucide-react';

const StrategicBlock = () => {
  return (
    <section
      className="py-12 md:py-16"
      style={{ backgroundColor: '#E9DFCF' }}
      data-testid="strategic-block-section"
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Titre principal */}
        <h3
          className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center leading-tight"
          style={{ 
            fontFamily: "'Georgia', 'Times New Roman', serif",
            letterSpacing: '-0.02em'
          }}
        >
          Dans le Golfe de Saint-Tropez, l'excellence n'est pas une option
          <span className="block mt-1">— c'est une exigence.</span>
        </h3>

        {/* Contenu éditorial dense */}
        <div className="space-y-5 text-gray-700 leading-relaxed mb-10">
          <p className="text-base md:text-lg">
            Dans le Golfe de Saint-Tropez, les entreprises évoluent dans un environnement où l'image, la réactivité 
            et la qualité de service conditionnent directement la performance. <strong>Hôtellerie, restauration, 
            immobilier, commerces, services</strong> : ici, chaque détail compte, chaque saison se prépare, et chaque 
            collaborateur doit être rapidement opérationnel.
          </p>

          <p className="text-base md:text-lg">
            Pourtant, de nombreuses TPE et PME locales avancent encore sans stratégie structurée de développement 
            des compétences. Les besoins montent vite, les équipes doivent s'adapter immédiatement, et les dirigeants 
            manquent souvent de temps pour prendre du recul, structurer leurs priorités et faire évoluer leurs 
            pratiques de façon durable.
          </p>

          <p className="text-base md:text-lg">
            Aujourd'hui, la performance repose sur <strong>trois leviers majeurs</strong> : un management clair, des outils 
            digitaux réellement maîtrisés, et une capacité concrète à intégrer l'intelligence artificielle dans les 
            usages métier. L'IA générative ne doit pas être un gadget ni un effet de mode : elle doit devenir un 
            levier simple, utile et rentable pour gagner du temps, fluidifier l'organisation, améliorer la relation 
            client et renforcer la qualité d'exécution.
          </p>
        </div>

        {/* 3 Piliers */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: '#1C2E4A' }}>
              <Users className="w-5 h-5 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Management clair</h4>
            <p className="text-sm text-gray-600">
              Structurer les équipes, clarifier les rôles, fluidifier la communication interne.
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: '#1C2E4A' }}>
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">Outils digitaux maîtrisés</h4>
            <p className="text-sm text-gray-600">
              Exploiter les bons outils, automatiser les tâches répétitives, gagner en efficacité.
            </p>
          </div>

          <div className="bg-white rounded-xl p-5 border border-gray-200 shadow-sm">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: '#1C2E4A' }}>
              <Cpu className="w-5 h-5 text-white" />
            </div>
            <h4 className="font-semibold text-gray-900 mb-2">IA intégrée aux métiers</h4>
            <p className="text-sm text-gray-600">
              Utiliser l'IA générative comme levier concret de productivité et de qualité.
            </p>
          </div>
        </div>

        {/* Suite du texte */}
        <div className="space-y-5 text-gray-700 leading-relaxed mb-10">
          <p className="text-base md:text-lg">
            Nos formations sont conçues pour répondre à cette réalité de terrain. Elles ne proposent pas une 
            approche théorique ou déconnectée, mais des <strong>solutions directement applicables</strong> dans l'activité 
            quotidienne. L'objectif est clair : faire monter rapidement vos équipes en compétence, sécuriser vos 
            méthodes de travail, professionnaliser vos pratiques et transformer vos contraintes en avantages opérationnels.
          </p>

          <p className="text-base md:text-lg font-medium text-gray-800">
            Former aujourd'hui, c'est mieux recruter, mieux intégrer, mieux manager et mieux performer demain. 
            Dans un territoire où tout va vite et où l'exigence client est élevée, investir dans les compétences 
            n'est pas un confort : c'est une décision stratégique.
          </p>
        </div>

        {/* Section "Vous vous reconnaissez ?" */}
        <div className="text-center pt-6 border-t border-gray-300">
          <h4 
            className="text-xl md:text-2xl font-semibold mb-3"
            style={{ color: '#1C2E4A' }}
          >
            Vous vous reconnaissez ?
          </h4>
          <p className="text-gray-600 mb-6 max-w-2xl mx-auto">
            Découvrez en 2 minutes votre niveau de maturité IA et recevez des recommandations personnalisées 
            pour structurer votre transformation digitale.
          </p>
          <a
            href="#diagnostic"
            className="inline-flex items-center justify-center px-8 py-3 rounded-full text-white font-semibold transition-all duration-300 hover:-translate-y-0.5"
            style={{ backgroundColor: '#1C2E4A' }}
          >
            Évaluez votre potentiel IA
          </a>
        </div>
      </div>
    </section>
  );
};

export default StrategicBlock;
